import { useEffect, useState } from "react";
import { addOnEventsChangeHandler, Event, flush } from "./eventRecorder";
import { format } from "@web-monorepo/dates";
import type { TranslatedString } from "@web-monorepo/i18next";

import { IconChevronDown, IconChevronUp, IconHelpCircleFilled } from "@web-monorepo/dds-icons";
import { DDSBodyText, DDSButton, DDSLabelText, DDSMicroText } from "@web-monorepo/dds";
import styles from "./EventLogHistorySection.module.css";

export const EventLogHistorySectionContainer = () => {
  const [eventsLog, setEventsLog] = useState<Event[]>([]);

  // register on mount to update the events list whenever the app sends a new event
  // eslint-disable-next-line @web-monorepo/prefer-useSideEffectOnMount -- the rule doesn't correctly handle the return type of this funciton
  useEffect(() => {
    return addOnEventsChangeHandler((events) => {
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setEventsLog(events.toReversed());
    });
  }, []);

  return <EventLogHistorySection eventsLog={eventsLog} clearEvents={flush} />;
};

// https://primer.style/components/relative-time
// eslint-disable-next-line react-refresh/only-export-components -- pre-existing violation
export function untranslatedFromNow(date: Date, skipSentence?: boolean): string {
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const seconds = Math.round(diff / 1000);

  if (Math.abs(seconds) < 60) {
    return "just now";
  }
  const minutes = Math.floor(seconds / 60);
  if (Math.abs(minutes) < 60) {
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(minutes, "minute");
  }
  const hours = Math.round(seconds / 3600);
  if (Math.abs(hours) < 24) {
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(hours, "hour");
  }
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 28) {
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(days, "day");
  }

  const dateString = format(date, { month: "short", day: "2-digit", year: days >= 365 ? "numeric" : undefined });
  if (skipSentence) return dateString;

  return `on ${dateString}`;
}

interface EventLogHistorySectionProps {
  eventsLog: Event[];
  clearEvents: VoidFunction;
}

const EventLogHistorySection = ({ eventsLog, clearEvents }: EventLogHistorySectionProps) => {
  return (
    <div className={styles.eventLogHistorySection}>
      <div className={styles.eventLogHistorySectionHeader}>
        <div className={styles.eventLogHistorySectionHeaderTitle}>
          <DDSLabelText>Logged Events</DDSLabelText>
        </div>
        <div className={styles.eventLogHistorySectionHeaderActions}>
          <DDSButton variant="unstyled" onClick={clearEvents} data-name="dev_tools_clear_events">
            <DDSMicroText className={styles.clearButton}>Clear logs</DDSMicroText>
          </DDSButton>
        </div>
      </div>
      <div className={styles.scrollArea}>
        <div className={styles.eventLogHistorySectionContent}>
          {eventsLog.map((event: Event) => {
            return <EventLogHistoryItem key={event._id} event={event} />;
          })}
        </div>
        <div className={styles.eventLogHistorySectionHelp}>
          <IconHelpCircleFilled size="m" />
          <div className={styles.eventLogHistorySectionHelpText}>
            <DDSBodyText level="md">
              Toolkit runs inside an iframe, therefore we don't have access to logged events. If you need to see Toolkit
              events, open the network tab of your developer toolbar.
            </DDSBodyText>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EventLogHistoryItemProps {
  event: Event;
}

const EventLogHistoryItem = ({ event }: EventLogHistoryItemProps) => {
  const [isShowingDetails, setIsShowingDetails] = useState(false);

  return (
    <div className={styles.eventLogHistoryItem}>
      {/* eslint-disable @web-monorepo/no-jsx-role-button -- pre-existing; expandable log entry */}
      <div
        className={styles.eventLogHistoryItemHeader}
        onClick={() => setIsShowingDetails((value) => !value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsShowingDetails((value) => !value);
          }
        }}
        role="button"
        tabIndex={0}
      >
        {/* eslint-enable @web-monorepo/no-jsx-role-button */}
        <div className={styles.eventLogHistoryItemHeaderContent}>
          <div>
            <DDSMicroText className={styles.microText}>
              {untranslatedFromNow(event.timestamp) as TranslatedString}
            </DDSMicroText>
          </div>
          <DDSBodyText className={styles.eventLogHistoryItemHeaderContentText}>
            {event.details.eventName as TranslatedString}
          </DDSBodyText>
          {event.details.eventValue ? (
            <div className={styles.eventLogHistoryItemHeaderContentValue}>
              <div className={styles.eventLogHistoryItemHeaderContentValueLabel}>
                <DDSMicroText className={styles.microText}>value:</DDSMicroText>
              </div>
              <div className={styles.eventLogHistoryItemHeaderContentValueValue}>
                <DDSMicroText className={styles.microText}>{event.details.eventValue}</DDSMicroText>
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.eventLogHistoryItemHeaderContentDetails}>
          <div className={styles.eventLogHistoryItemHeaderContentDetailsIcon}>
            {isShowingDetails ? <IconChevronUp size="s" /> : <IconChevronDown size="s" />}
          </div>
        </div>
      </div>
      {isShowingDetails ? (
        <div className={styles.eventLogHistoryItemHeaderContentDetailsContent}>
          <pre aria-label="Log event details">{JSON.stringify(event.details, null, 2)}</pre>
        </div>
      ) : null}
    </div>
  );
};
